import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import api from "../../components/api";
import NavBar from "../../components/NavBar/NavBar";
import SideBar from "../../components/SideBar/SideBar";
import "./WeddingSeatingChartPage.css";
import { Table, Guest } from "../../api";
import roundTable from "../../icons/round_table.svg";
import longTable from "../../icons/long_table.svg";
import stage from "../../icons/stage.svg";
import circle from "../../icons/circle.svg";
import curvedTable from "../../icons/curved_table.svg";
import danceFloor from "../../icons/dance_floor.svg";
import "../../fonts/fonts.css";
import DownloadHandler from "../../components/SeatingChart/DownloadHandler";
import StyleSidebar from "../../components/SeatingChart/StyleSidebar";
import DraggableImage from "../../components/SeatingChart/DraggableImage";

const GRID_SIZE = 20; // 网格在屏幕上的像素大小
const REAL_GRID_SIZE = 50; // 每个网格代表的实际厘米数

export const tableStyleMap = {
  1: roundTable,
  2: longTable,
  3: stage,
  4: circle,
  5: curvedTable,
  6: danceFloor,
};

export const defaultTableImage = roundTable;

// 在文件顶部添加类型定义
interface ChartStyle {
  id: string |  number;
  name: string;
  titleFont: string;
  titleSize: string;
  coupleNamesFont: string;
  coupleNamesSize: string;
  dateFont: string;
  dateSize: string;
  tableFont: string;
  tableFontSize: string;
  guestListFont: string;
  guestListFontSize: string;
  layout?: {
    header: {
      width: number;
      height: number;
    };
    table: {
      width: number;
      height: number;
    };
  };
}

interface ImageItem {
  id: string;
  url: string;
  x: number;
  y: number;
  width: number;
  height: number;
  styleId?: number; // 添加可选的styleId属性
}

const fontOptions = [
  { value: "Dancing Script", label: "Dancing Script" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Lora", label: "Lora" },
  { value: "Pacifico", label: "Pacifico" },
  { value: "Satisfy", label: "Satisfy" },
  { value: "Mea Culpa", label: "Mea Culpa" },
  { value: "Fleur De Leah", label: "Fleur De Leah" },
];

// 在组件内添加默认样式
const defaultStyles: ChartStyle[] = [
  {
    id: 1,
    name: "经典样式",
    titleFont: "Dancing Script",
    titleSize: "32px",
    coupleNamesFont: "Lora",
    coupleNamesSize: "24px",
    dateFont: "Lora",
    dateSize: "18px",
    tableFont: "Lora",
    tableFontSize: "16px",
    guestListFont: "Lora",
    guestListFontSize: "14px",
  },
  {
    id: 2,
    name: "纸片样式",
    titleFont: "Dancing Script",
    titleSize: "28px",
    coupleNamesFont: "Lora",
    coupleNamesSize: "22px",
    dateFont: "Lora",
    dateSize: "16px",
    tableFont: "Lora",
    tableFontSize: "16px",
    guestListFont: "Lora",
    guestListFontSize: "14px",
    layout: {
      header: {
        width: 320,
        height: 200,
      },
      table: {
        width: 180,
        height: 300,
      },
    },
  },
];

// 添加一个防抖函数
const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

function WeddingSeatingChartPage() {
  const { weddingTableId } = useParams();
  const navigate = useNavigate();
  const [tables, setTables] = useState<Table[]>([]);
  const [title, setTitle] = useState("Please take your seat");
  const [coupleNames, setCoupleNames] = useState("新郎 & 新娘");
  const [backgroundImage, setBackgroundImage] = useState("default-bg.jpg");
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [layoutHeight, setLayoutHeight] = useState(0);
  const [tableLayouts, setTableLayouts] = useState<
    Array<{
      id: string;
      name: string;
      x: number;
      y: number;
      width: number;
      height: number;
      imageUrl: string;
    }>
  >([]);
  const [scale, setScale] = useState(1);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [weddingDate, setWeddingDate] = useState("");
  const [visibleTableOrder, setVisibleTableOrder] = useState<string[]>([]);
  const [images, setImages] = useState<ImageItem[]>([]);
  const [history, setHistory] = useState<
    Array<{
      tables: Table[];
      visibleTableOrder: string[];
      images: Array<{
        id: string;
        url: string;
        x: number;
        y: number;
        width: number;
        height: number;
      }>;
      title: string;
      coupleNames: string;
      weddingDate: string;
      paperStyleVisibleTables?: string[];
    }>
  >([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [showTables, setShowTables] = useState(true);
  const [isStyleSidebarOpen, setIsStyleSidebarOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState<ChartStyle>(
    defaultStyles[0],
  );
  const [paperStyleVisibleTables, setPaperStyleVisibleTables] = useState<string[]>([]);

  useEffect(() => {
    if (weddingTableId) {
      // 获取婚礼布局信息
      api.weddings
        .getWeddingWeddingTableWeddingTableIdGet(weddingTableId)
        .then((weddingResponse) => {
          const orderId = weddingResponse.data.order_id;

          // 获取所有桌子
          api.tables
            .getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(weddingTableId)
            .then((tablesResponse) => {
              const allTables = tablesResponse.data.contents.map((table) => {
                try {
                  if (table.table_style) {
                    const style = JSON.parse(table.table_style);
                    const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
                    return {
                      ...table,
                      ...style,
                      x: style.x * pixelsPerCm,
                      y: style.y * pixelsPerCm,
                      width: style.width * pixelsPerCm,
                      height: style.height * pixelsPerCm,
                    };
                  }
                } catch (e) {
                  console.warn("Invalid table_style JSON:", e);
                }

                return {
                  ...table,
                  x: 0,
                  y: 0,
                  width: 80,
                  height: 80,
                  imageUrl: roundTable,
                };
              });
              setTables(allTables);

              // 先检查是否有保存的座位图样式
              if (weddingResponse.data.seating_chart_style) {
                const style = JSON.parse(
                  weddingResponse.data.seating_chart_style,
                );
                if (style.title) setTitle(style.title);
                if (style.coupleNames) {
                  setCoupleNames(style.coupleNames);
                } else {
                  // 如果没有保存的新郎新娘名字，才从订单信息获取
                  api.orders
                    .getOrderOrdersOrderIdGet(orderId)
                    .then((orderResponse) => {
                      const users = orderResponse.data.users as Array<{
                        first_name: string;
                        last_name: string;
                      }>;
                      if (users && users.length >= 2) {
                        const couple = users
                          .map((user) => `${user.first_name} ${user.last_name}`)
                          .join(" & ");
                        setCoupleNames(couple);
                      }
                    });
                }
                if (style.weddingDate) setWeddingDate(style.weddingDate);
                if (style.tableOrder) {
                  // 验证保存的桌子顺序是否都存在
                  const validTableIds = style.tableOrder.filter((id: string) =>
                    allTables.some((table) => table.table_id === id),
                  );
                  setVisibleTableOrder(validTableIds);
                } else {
                  // 如果没有保存的顺序，使用所有桌子的ID作为默认顺序
                  setVisibleTableOrder(
                    allTables.map((table) => table.table_id),
                  );
                }
                if (style.images) setImages(style.images);
                if (style.backgroundImage)
                  setBackgroundImage(style.backgroundImage);
                
                // 加载字体和字号设置
                if (style.fontSettings) {
                  // 创建一个新的样式对象，基于默认样式但使用保存的字体设置
                  const savedStyleId = style.styleId || 1; // 如果没有保存styleId，默认使用1
                  const baseStyle = defaultStyles.find(s => s.id === savedStyleId) || defaultStyles[0];
                  
                  setSelectedStyle({
                    ...baseStyle,
                    titleFont: style.fontSettings.titleFont || baseStyle.titleFont,
                    titleSize: style.fontSettings.titleSize || baseStyle.titleSize,
                    coupleNamesFont: style.fontSettings.coupleNamesFont || baseStyle.coupleNamesFont,
                    coupleNamesSize: style.fontSettings.coupleNamesSize || baseStyle.coupleNamesSize,
                    dateFont: style.fontSettings.dateFont || baseStyle.dateFont,
                    dateSize: style.fontSettings.dateSize || baseStyle.dateSize,
                    tableFont: style.fontSettings.tableFont || baseStyle.tableFont,
                    tableFontSize: style.fontSettings.tableFontSize || baseStyle.tableFontSize,
                    guestListFont: style.fontSettings.guestListFont || baseStyle.guestListFont,
                    guestListFontSize: style.fontSettings.guestListFontSize || baseStyle.guestListFontSize,
                  });
                }

                // 初始化纸片样式可见桌子
                if (style.paperStyleVisibleTables) {
                  setPaperStyleVisibleTables(style.paperStyleVisibleTables);
                } else {
                  // 如果没有保存的状态，默认所有桌子都显示
                  setPaperStyleVisibleTables(allTables.map(table => table.table_id));
                }
              } else {
                // 如果没有保存的座位图样式，使用所有桌子的ID作为默认顺序
                setVisibleTableOrder(allTables.map((table) => table.table_id));

                // 从订单信息获取新郎新娘名字
                api.orders
                  .getOrderOrdersOrderIdGet(orderId)
                  .then((orderResponse) => {
                    const users = orderResponse.data.users as Array<{
                      first_name: string;
                      last_name: string;
                    }>;
                    if (users && users.length >= 2) {
                      const couple = users
                        .map((user) => `${user.first_name} ${user.last_name}`)
                        .join(" & ");
                      setCoupleNames(couple);
                    }
                  });

                // 默认所有桌子都显示
                setPaperStyleVisibleTables(allTables.map(table => table.table_id));
              }
            });

          if (weddingResponse.data.wedding_style) {
            const wedding_style = JSON.parse(
              weddingResponse.data.wedding_style,
            );
            const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
            setLayoutWidth(wedding_style.width * pixelsPerCm);
            setLayoutHeight(wedding_style.height * pixelsPerCm);
          }

          // 添加日期处理
          if (weddingResponse.data.wedding_date) {
            const date = new Date(weddingResponse.data.wedding_date);
            const formattedDate = `${date.getFullYear()}.${date.getMonth() + 1}.${date.getDate()}`;
            setWeddingDate(formattedDate);
          }

          // 获取宾客信息
          api.guests
            .getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(weddingTableId)
            .then((data) => {
              console.log('获取的宾客数据:', data.data.contents);
              setGuests(data.data.contents);
            })
            .catch(error => {
              console.error('获取宾客数据失败:', error);
            });
        });
    }
  }, [weddingTableId]);

  useEffect(() => {
    if (layoutWidth && layoutHeight) {
      const targetWidth = 300; // 目标宽度为300像素
      const scale = targetWidth / layoutWidth; // 计算缩放比例
      setScale(scale);
    }
  }, [layoutWidth, layoutHeight]);

  useEffect(() => {
    if (tables.length > 0 && visibleTableOrder.length > 0) {
      // 检查是否已经初始化过历史记录
      if (history.length === 0) {
        // 等待所有数据加载完成
        Promise.all([
          new Promise((resolve) => setTimeout(resolve, 100)), // 给图片加载一些时间
        ]).then(() => {
          const initialState = {
            tables: [...tables],
            visibleTableOrder: [...visibleTableOrder],
            images: [...images],
            title,
            coupleNames,
            weddingDate,
            paperStyleVisibleTables: [...paperStyleVisibleTables],
          };
          setHistory([initialState, initialState]);
          setCurrentHistoryIndex(1);
        });
      }
    }
  }, [tables.length, visibleTableOrder.length, images.length, paperStyleVisibleTables.length]);

  useEffect(() => {
    const chartTitle = document.querySelector(".chart-title") as HTMLElement;
    const coupleNames = document.querySelector(".couple-names") as HTMLElement;
    const weddingDate = document.querySelector(".wedding-date") as HTMLElement;
    const tableNames = document.querySelectorAll(".table-name");
    const guestItems = document.querySelectorAll(".guest-item");

    if (chartTitle) {
      chartTitle.style.fontFamily = selectedStyle.titleFont;
      chartTitle.style.fontSize = selectedStyle.titleSize;
    }
    if (coupleNames) {
      coupleNames.style.fontFamily = selectedStyle.coupleNamesFont;
      coupleNames.style.fontSize = selectedStyle.coupleNamesSize;
    }
    if (weddingDate) {
      weddingDate.style.fontFamily = selectedStyle.dateFont;
      weddingDate.style.fontSize = selectedStyle.dateSize;
    }
    tableNames.forEach((table) => {
      (table as HTMLElement).style.fontFamily = selectedStyle.tableFont;
      (table as HTMLElement).style.fontSize = selectedStyle.tableFontSize;
    });
    guestItems.forEach((guest) => {
      (guest as HTMLElement).style.fontFamily = selectedStyle.guestListFont;
      (guest as HTMLElement).style.fontSize = selectedStyle.guestListFontSize;
    });
  }, [selectedStyle]);

  const handleBackgroundUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setBackgroundImage((e.target as FileReader).result as string);
        setSelectedFileName(file.name);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveBackground = () => {
    setBackgroundImage("default-bg.jpg");
    setSelectedFileName("");
  };

  const handleDragStart = (
    e: React.DragEvent,
    tableId: string,
    side: "left" | "right",
  ) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ tableId, side }));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (
    e: React.DragEvent,
    targetTableId: string,
    targetSide: "left" | "right",
  ) => {
    e.preventDefault();
    const data = JSON.parse(e.dataTransfer.getData("text/plain"));
    const { tableId: draggedTableId, side: sourceSide } = data;

    setVisibleTableOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const draggedIndex = newOrder.indexOf(draggedTableId);
      const targetIndex = newOrder.indexOf(targetTableId);

      // 如果是跨边拖拽，需要调整目标位置
      if (sourceSide !== targetSide) {
        const middleIndex = Math.ceil(newOrder.length / 2);
        const adjustedTargetIndex =
          targetSide === "right"
            ? Math.max(middleIndex, targetIndex)
            : Math.min(middleIndex - 1, targetIndex);

        newOrder.splice(draggedIndex, 1);
        newOrder.splice(adjustedTargetIndex, 0, draggedTableId);
      } else {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedTableId);
      }

      // 在这里手动调用saveToHistory以记录这次操作
      setTimeout(() => saveToHistory(tables, newOrder, images), 0);
      
      return newOrder;
    });
  };

  const handleRemoveFromList = (tableId: string) => {
    if (window.confirm("确定要从列表中移除这个桌子吗？")) {
      const newOrder = visibleTableOrder.filter((id) => id !== tableId);
      setVisibleTableOrder(newOrder);
      saveToHistory(tables, newOrder, images);
    }
  };

  const handleWeddingDateChange = (e: React.FormEvent<HTMLDivElement>) => {
    const newDate = e.currentTarget.textContent || "";
    setWeddingDate(newDate);

    const newState = {
      tables,
      visibleTableOrder,
      images,
      title,
      coupleNames,
      weddingDate: newDate,
      paperStyleVisibleTables: [...paperStyleVisibleTables],
    };

    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleSave = async () => {
    if (weddingTableId) {
      const style = {
        title,
        coupleNames,
        weddingDate,
        tableOrder: visibleTableOrder,
        images,
        backgroundImage,
        // 添加纸片样式可见桌子状态
        paperStyleVisibleTables,
        // 添加字体和字号相关设置
        fontSettings: {
          titleFont: selectedStyle.titleFont,
          titleSize: selectedStyle.titleSize,
          coupleNamesFont: selectedStyle.coupleNamesFont,
          coupleNamesSize: selectedStyle.coupleNamesSize,
          dateFont: selectedStyle.dateFont,
          dateSize: selectedStyle.dateSize,
          tableFont: selectedStyle.tableFont,
          tableFontSize: selectedStyle.tableFontSize,
          guestListFont: selectedStyle.guestListFont,
          guestListFontSize: selectedStyle.guestListFontSize,
        },
        // 保存当前选择的样式ID
        styleId: selectedStyle.id
      };

      await api.weddings.patchWeddingsWeddingTableIdWeddingTableWeddingTableIdPatch(
        weddingTableId,
        {
          seating_chart_style: JSON.stringify(style),
        },
      );
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now().toString(),
          url: (e.target as FileReader).result as string,
          x: 100,
          y: 100,
          width: 200,
          height: 200,
          styleId: Number(selectedStyle.id), // 添加样式ID
        };
        const newImages = [...images, newImage];
        setImages(newImages);
        saveToHistory(tables, visibleTableOrder, newImages);
      };
      reader.readAsDataURL(file);
    }
  };

  // 修改图片拖动处理函数
  const handleImageDrag = (imageId: string, x: number, y: number) => {
    const newImages = images.map((img) =>
      img.id === imageId ? { ...img, x, y } : img,
    );
    setImages(newImages);
  };

  // 添加拖动结束时的处理函数
  const handleImageDragEnd = debounce((imageId: string, x: number, y: number) => {
    const newImages = images.map((img) =>
      img.id === imageId ? { ...img, x, y } : img,
    );
    saveToHistory(tables, visibleTableOrder, newImages);
  }, 300);

  // 修改图片大小调整处理函数
  const handleImageResize = (
    imageId: string,
    width: number,
    height: number,
  ) => {
    const newImages = images.map((img) =>
      img.id === imageId ? { ...img, width, height } : img,
    );
    setImages(newImages);
  };

  // 添加大小调整结束时的处理函数
  const handleImageResizeEnd = debounce((
    imageId: string,
    width: number,
    height: number,
  ) => {
    const newImages = images.map((img) =>
      img.id === imageId ? { ...img, width, height } : img,
    );
    saveToHistory(tables, visibleTableOrder, newImages);
  }, 300);

  const handleTitleChange = (e: React.FormEvent<HTMLDivElement>) => {
    const newTitle = e.currentTarget.textContent || "";
    setTitle(newTitle);

    const newState = {
      tables,
      visibleTableOrder,
      images,
      title: newTitle,
      coupleNames,
      weddingDate,
      paperStyleVisibleTables: [...paperStyleVisibleTables],
    };

    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleCoupleNamesChange = (e: React.FormEvent<HTMLDivElement>) => {
    const newNames = e.currentTarget.textContent || "";
    setCoupleNames(newNames);

    const newState = {
      tables,
      visibleTableOrder,
      images,
      title,
      coupleNames: newNames,
      weddingDate,
      paperStyleVisibleTables: [...paperStyleVisibleTables],
    };

    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleDeleteImage = (imageId: string) => {
    if (window.confirm("确定要删除这张图片吗？")) {
      const newImages = images.filter((img) => img.id !== imageId);
      setImages(newImages);
      saveToHistory(tables, visibleTableOrder, newImages);
    }
  };

  const saveToHistory = (
    newTables: Table[],
    newVisibleTableOrder: string[],
    newImages: typeof images = images,
  ) => {
    const newState = {
      tables: newTables,
      visibleTableOrder: newVisibleTableOrder,
      images: newImages,
      title,
      coupleNames,
      weddingDate,
      paperStyleVisibleTables: [...paperStyleVisibleTables],
    };

    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  const handleUndo = () => {
    if (currentHistoryIndex > 1) {
      const previousState = history[currentHistoryIndex - 1];
      setTables(previousState.tables);
      setVisibleTableOrder(previousState.visibleTableOrder);
      setImages(previousState.images);
      setTitle(previousState.title);
      setCoupleNames(previousState.coupleNames);
      setWeddingDate(previousState.weddingDate);
      // 恢复纸片样式可见桌子状态
      if (previousState.paperStyleVisibleTables) {
        setPaperStyleVisibleTables(previousState.paperStyleVisibleTables);
      }
      setCurrentHistoryIndex(currentHistoryIndex - 1);
    } else if (currentHistoryIndex === 1) {
      const initialState = history[0];
      setTables(initialState.tables);
      setVisibleTableOrder(initialState.visibleTableOrder);
      setImages(initialState.images);
      setTitle(initialState.title);
      setCoupleNames(initialState.coupleNames);
      setWeddingDate(initialState.weddingDate);
      setCurrentHistoryIndex(1);
    }
  };

  const handleDragEnd = (tableId: string, x: number, y: number) => {
    const newTables = tables.map((table) => {
      if (table.table_id === tableId) {
        return { ...table, x, y };
      }
      return table;
    });
    setTables(newTables);
    saveToHistory(newTables, visibleTableOrder, images);
  };

  // 添加重置桌子顺序的函数
  const handleResetTableOrder = () => {
    // 重置为所有桌子的默认顺序
    setVisibleTableOrder(tables.map((table) => table.table_id));
  };

  // 添加重置背景图片的函数
  const handleResetBackground = () => {
    // 重置背景图片为默认值
    setBackgroundImage("default-bg.jpg");
    setSelectedFileName("");
  };

  // 添加样式选择处理函数
  const handleStyleSelect = (style: ChartStyle) => {
    // 保存当前状态到历史记录
    const currentState = {
      tables,
      visibleTableOrder,
      images,
      title,
      coupleNames,
      weddingDate,
      selectedStyle,
      paperStyleVisibleTables: [...paperStyleVisibleTables],
    };
    
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(currentState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
    
    // 设置新选择的样式
    setSelectedStyle(style);
  };

  // 修改删除桌子纸片的处理函数
  const handleDeleteTableCard = (tableId: string) => {
    if (window.confirm("确定要在纸片样式中隐藏这个桌子吗？")) {
      const newVisibleTables = paperStyleVisibleTables.filter((id) => id !== tableId);
      setPaperStyleVisibleTables(newVisibleTables);
      
      // 保存到历史记录中
      const newState = {
        tables,
        visibleTableOrder,
        images,
        title,
        coupleNames,
        weddingDate,
        paperStyleVisibleTables: newVisibleTables,
      };
      
      const newHistory = history.slice(0, currentHistoryIndex + 1);
      newHistory.push(newState);
      setHistory(newHistory);
      setCurrentHistoryIndex(newHistory.length - 1);
    }
  };

  // 添加一个函数来处理显示所有桌子纸片
  const handleShowAllTableCards = () => {
    // 重置为显示所有桌子
    setPaperStyleVisibleTables(tables.map(table => table.table_id));
    // 保存到历史记录
    const newState = {
      tables,
      visibleTableOrder,
      images,
      title,
      coupleNames,
      weddingDate,
      paperStyleVisibleTables: tables.map(table => table.table_id)
    };
    const newHistory = history.slice(0, currentHistoryIndex + 1);
    newHistory.push(newState);
    setHistory(newHistory);
    setCurrentHistoryIndex(newHistory.length - 1);
  };

  return (
    <div className="seating-chart-page">
      <div className="button-container">
        <Button
          variant="outline-primary"
          onClick={() => navigate(`/wedding_table/${weddingTableId}/table-list`)}
        >
          <i className="fa fa-angle-left"></i> Back
        </Button>
        <div className="upload-container">
          <label className="upload-label">
            <Button variant="outline-primary" as="span">
              Choose Background
            </Button>
            {selectedFileName && (
              <span className="file-name">
                {selectedFileName}
                <button
                  className="remove-file"
                  onClick={handleRemoveBackground}
                >
                  ×
                </button>
              </span>
            )}
            <input
              type="file"
              accept="image/*"
              onChange={handleBackgroundUpload}
              className="background-upload"
              hidden
            />
          </label>
        </div>
        <Button variant="outline-primary" onClick={handleSave}>
          Save
        </Button>
        <DownloadHandler 
          weddingTableId={weddingTableId || ""}
          title={title}
          coupleNames={coupleNames}
          weddingDate={weddingDate}
          tables={tables}
          guests={guests}
          tableLayouts={tableLayouts}
          visibleTableOrder={visibleTableOrder}
          selectedStyle={selectedStyle}
          showTables={showTables}
          layoutWidth={layoutWidth}
          layoutHeight={layoutHeight}
          scale={scale}
          images={images}
        />
        <div className="upload-container">
          <label className="upload-label">
            <Button variant="outline-primary" as="span">
              Insert Image
            </Button>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="image-upload"
              hidden
            />
          </label>
        </div>
        <Button
          variant="outline-primary"
          onClick={handleUndo}
          disabled={currentHistoryIndex <= 0}
        >
          Revocation
        </Button>
        <Button
          variant="outline-primary"
          onClick={() => setIsStyleSidebarOpen(!isStyleSidebarOpen)}
        >
          编辑样式
        </Button>
      </div>

      <div
        className={`chart-container ${selectedStyle.id === 2 ? "paper-style" : ""}`}
      >
        {selectedStyle.id === 2 ? (
          // 纸片样式布局
          <>
            {/* 渲染所有上传的图片 */}
            {images
            .filter((image) => (image as any).styleId === selectedStyle.id || !selectedStyle.id)
            .map((image) => (
              <DraggableImage
                key={image.id}
                id={image.id}
                url={image.url}
                x={image.x}
                y={image.y}
                width={image.width}
                height={image.height}
                onDrag={handleImageDrag}
                onDragEnd={handleImageDragEnd}
                onResize={handleImageResize}
                onResizeEnd={handleImageResizeEnd}
                onDelete={handleDeleteImage}
              />
            ))}
            
            {/* 标题纸片 */}
            <div
              className="paper-card header-card"
              style={{
                width: selectedStyle.layout?.header.width,
                height: selectedStyle.layout?.header.height,
                position: "relative",
              }}
            >
              <button
                className="delete-paper-btn"
                onClick={() => {
                  if (window.confirm("确定要重置标题信息吗？")) {
                    setTitle("婚礼座位图");
                    setCoupleNames("新人姓名");
                    setWeddingDate("婚礼日期");
                    saveToHistory(tables, visibleTableOrder, images);
                  }
                }}
                title="重置标题"
              >
                ×
              </button>
              <div className="chart-title">{title}</div>
              <div className="couple-names">{coupleNames}</div>
              <div className="wedding-date">{weddingDate}</div>
            </div>

            {/* 每个桌子的纸片 */}
            {tables
              .filter(table => paperStyleVisibleTables.includes(table.table_id))
              .map((table) => (
                <div
                  key={table.table_id}
                  className="paper-card table-card"
                  style={{
                    width: selectedStyle.layout?.table.width,
                    height: selectedStyle.layout?.table.height,
                    position: "relative",
                  }}
                >
                  <button
                    className="delete-paper-btn"
                    onClick={() => handleDeleteTableCard(table.table_id)}
                    title="在纸片样式中隐藏"
                  >
                    ×
                  </button>
                  <div className="table-name">{table.table_name}</div>
                  <div className="guest-list">
                    {guests
                      .filter((guest) => guest.table_id === table.table_id)
                      .map((guest) => (
                        <div key={guest.guest_id} className="guest-item">
                          {guest.guest_name}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
          </>
        ) : (
          // 经典样式布局保持不变
          <div
            className="chart-background"
            style={{
              backgroundImage: `url(${backgroundImage})`,
            }}
          >
            {/* 渲染所有上传的图片 */}
            {images
            .filter((image) => (image as any).styleId === selectedStyle.id || !selectedStyle.id)
            .map((image) => (
              <DraggableImage
                key={image.id}
                id={image.id}
                url={image.url}
                x={image.x}
                y={image.y}
                width={image.width}
                height={image.height}
                onDrag={handleImageDrag}
                onDragEnd={handleImageDragEnd}
                onResize={handleImageResize}
                onResizeEnd={handleImageResizeEnd}
                onDelete={handleDeleteImage}
              />
            ))}
            
            <div className="header-section">
              <div
                className="chart-title"
                contentEditable={true}
                onBlur={handleTitleChange}
              >
                {title}
              </div>
              <div
                className="couple-names"
                contentEditable={true}
                onBlur={handleCoupleNamesChange}
              >
                {coupleNames}
              </div>
              <div
                className="wedding-date"
                contentEditable={true}
                onBlur={handleWeddingDateChange}
              >
                {weddingDate}
              </div>
            </div>

            <div
              className={`content-section ${!showTables ? "hide-tables" : ""}`}
            >
              <div className="guest-lists">
                <div className="guest-column">
                  {visibleTableOrder
                    .slice(0, Math.ceil(visibleTableOrder.length / 4))
                    .map((tableId) => {
                      const table = tables.find((t) => t.table_id === tableId);
                      if (!table) return null;
                      const tableGuests = guests.filter(
                        (guest) => guest.table_id === table.table_id,
                      );
                      return (
                        <div
                          key={table.table_id}
                          className="table-info"
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, table.table_id, "left")
                          }
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, table.table_id, "left")}
                        >
                          <div className="table-info-content">
                            <div className="table-content-wrapper">
                              <div className="table-name">
                                {table.table_name}
                              </div>
                              <div className="guest-list">
                                {tableGuests.length > 0 ? (
                                  tableGuests.map((guest) => (
                                    <div
                                      key={guest.guest_id}
                                      className="guest-item"
                                    >
                                      {showTables
                                        ? `${guest.guest_name}(${guest.guest_count}p)`
                                        : guest.guest_name}
                                    </div>
                                  ))
                                ) : (
                                  <div className="guest-item">No Guests</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            className="delete-guest-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromList(table.table_id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                </div>
                <div className="guest-column">
                  {visibleTableOrder
                    .slice(
                      Math.ceil(visibleTableOrder.length / 4),
                      Math.ceil(visibleTableOrder.length / 2),
                    )
                    .map((tableId) => {
                      const table = tables.find((t) => t.table_id === tableId);
                      if (!table) return null;
                      const tableGuests = guests.filter(
                        (guest) => guest.table_id === table.table_id,
                      );
                      return (
                        <div
                          key={table.table_id}
                          className="table-info"
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, table.table_id, "left")
                          }
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, table.table_id, "left")}
                        >
                          <div className="table-info-content">
                            <div className="table-content-wrapper">
                              <div className="table-name">
                                {table.table_name}
                              </div>
                              <div className="guest-list">
                                {tableGuests.length > 0 ? (
                                  tableGuests.map((guest) => (
                                    <div
                                      key={guest.guest_id}
                                      className="guest-item"
                                    >
                                      {showTables
                                        ? `${guest.guest_name}(${guest.guest_count}p)`
                                        : guest.guest_name}
                                    </div>
                                  ))
                                ) : (
                                  <div className="guest-item">No Guests</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            className="delete-guest-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromList(table.table_id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className="center-layout-container">
                <div
                  className={`center-layout ${showTables ? "show-grid" : ""}`}
                  style={{
                    width: layoutWidth * scale,
                    height: layoutHeight * scale,
                  }}
                >
                  {showTables &&
                    tables.map((table) => {
                      let tableStyle;
                      try {
                        tableStyle = table.table_style
                          ? JSON.parse(table.table_style)
                          : null;
                      } catch (e) {
                        console.warn("Invalid table_style JSON:", e);
                      }

                      const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
                      const x = (tableStyle?.x || 0) * pixelsPerCm;
                      const y = (tableStyle?.y || 0) * pixelsPerCm;
                      const width = (tableStyle?.width || 80) * pixelsPerCm;
                      const height = (tableStyle?.height || 80) * pixelsPerCm;
                      const isStage = tableStyle?.imageUrl?.includes("stage");

                      return (
                        <div
                          key={table.table_id}
                          style={{
                            position: "absolute",
                            left: x * scale,
                            top: y * scale,
                            width: width * scale,
                            height: height * scale,
                            transform: `rotate(${tableStyle?.rotation || 0}deg) scaleX(${tableStyle?.flipX ? -1 : 1}) scaleY(${tableStyle?.flipY ? -1 : 1})`,
                          }}
                          className="table-layout-item"
                          data-is-stage={isStage}
                        >
                          <img
                            src={tableStyle?.imageUrl || defaultTableImage}
                            alt={table.table_name}
                            style={{ width: "100%", height: "100%" }}
                          />
                          <div 
                            className="table-layout-name"
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: `translate(-50%, -50%) scaleX(${tableStyle?.flipX ? -1 : 1}) scaleY(${tableStyle?.flipY ? -1 : 1})`,
                              width: '100%',
                              textAlign: 'center'
                            }}
                          >
                            {table.table_name}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>

              <div className="guest-lists">
                <div className="guest-column">
                  {visibleTableOrder
                    .slice(
                      Math.ceil(visibleTableOrder.length / 2),
                      Math.ceil((visibleTableOrder.length * 3) / 4),
                    )
                    .map((tableId) => {
                      const table = tables.find((t) => t.table_id === tableId);
                      if (!table) return null;
                      const tableGuests = guests.filter(
                        (guest) => guest.table_id === table.table_id,
                      );
                      return (
                        <div
                          key={table.table_id}
                          className="table-info"
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, table.table_id, "right")
                          }
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, table.table_id, "right")}
                        >
                          <div className="table-info-content">
                            <div className="table-content-wrapper">
                              <div className="table-name">
                                {table.table_name}
                              </div>
                              <div className="guest-list">
                                {tableGuests.length > 0 ? (
                                  tableGuests.map((guest) => (
                                    <div
                                      key={guest.guest_id}
                                      className="guest-item"
                                    >
                                      {showTables
                                        ? `${guest.guest_name}(${guest.guest_count}p)`
                                        : guest.guest_name}
                                    </div>
                                  ))
                                ) : (
                                  <div className="guest-item">No Guests</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            className="delete-guest-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromList(table.table_id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                </div>
                <div className="guest-column">
                  {visibleTableOrder
                    .slice(Math.ceil((visibleTableOrder.length * 3) / 4))
                    .map((tableId) => {
                      const table = tables.find((t) => t.table_id === tableId);
                      if (!table) return null;
                      const tableGuests = guests.filter(
                        (guest) => guest.table_id === table.table_id,
                      );
                      return (
                        <div
                          key={table.table_id}
                          className="table-info"
                          draggable
                          onDragStart={(e) =>
                            handleDragStart(e, table.table_id, "right")
                          }
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, table.table_id, "right")}
                        >
                          <div className="table-info-content">
                            <div className="table-content-wrapper">
                              <div className="table-name">
                                {table.table_name}
                              </div>
                              <div className="guest-list">
                                {tableGuests.length > 0 ? (
                                  tableGuests.map((guest) => (
                                    <div
                                      key={guest.guest_id}
                                      className="guest-item"
                                    >
                                      {showTables
                                        ? `${guest.guest_name}(${guest.guest_count}p)`
                                        : guest.guest_name}
                                    </div>
                                  ))
                                ) : (
                                  <div className="guest-item">No Guests</div>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            className="delete-guest-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromList(table.table_id);
                            }}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <StyleSidebar 
        isOpen={isStyleSidebarOpen}
        setIsOpen={setIsStyleSidebarOpen}
        selectedStyle={selectedStyle}
        setSelectedStyle={setSelectedStyle}
        showTables={showTables}
        setShowTables={setShowTables}
        fontOptions={fontOptions}
        defaultStyles={defaultStyles}
        resetTableOrder={handleResetTableOrder}
        resetBackground={handleResetBackground}
        handleStyleSelect={handleStyleSelect}
        showAllTableCards={handleShowAllTableCards}
      />
    </div>
  );
}

export default WeddingSeatingChartPage;
